import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Shield, Plus, Power, Key, Copy, Trash2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminManagers() {
  const { token, user } = useAuth();
  const { currentTemplate } = useTheme();
  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ first_name: '', last_name: '', email: '', password: '' });
  const [editPassword, setEditPassword] = useState({ id: null, password: '' });

  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users?role=admin_ops`, { headers: { Authorization: `Bearer ${token}` } });
      setAdmins(res.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleCreate = async () => {
    if (!newAdmin.email || !newAdmin.password) { toast.error('Email et mot de passe requis'); return; }
    try {
      await axios.post(`${API}/auth/register`, { ...newAdmin, role: 'admin_ops' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Administrateur créé');
      setShowCreate(false);
      setNewAdmin({ first_name: '', last_name: '', email: '', password: '' });
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleToggleStatus = async (admin) => {
    const newStatus = admin.status === 'active' ? 'blocked' : 'active';
    if (!window.confirm(admin.status === 'active' ? `Désactiver ${admin.first_name} ${admin.last_name} ?` : `Réactiver ${admin.first_name} ${admin.last_name} ?`)) return;
    try {
      await axios.put(`${API}/clients/${admin.id}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(admin.status === 'active' ? 'Admin désactivé' : 'Admin réactivé');
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteAdmin = async (admin) => {
    if (!window.confirm(`Supprimer définitivement ${admin.first_name} ${admin.last_name} ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/users/${admin.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin supprimé');
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleSetPassword = async () => {
    if (!editPassword.id || !editPassword.password || editPassword.password.length < 6) {
      toast.error('Mot de passe trop court (min 6)'); return;
    }
    try {
      await axios.put(`${API}/clients/${editPassword.id}/set-password`, {
        password: editPassword.password, force_change: false
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Mot de passe mis à jour');
      setEditPassword({ id: null, password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (user?.role !== 'super_admin') {
    return <DashboardLayout><p className="text-center py-20 text-muted-foreground">Accès réservé au Super Admin</p></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-managers-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Administrateurs</h1>
            <p className="text-sm text-muted-foreground">{admins.length} administrateur{admins.length > 1 ? 's' : ''}</p>
          </div>
          <Button onClick={() => setShowCreate(true)} style={{ backgroundColor: primaryColor }} data-testid="create-admin-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvel administrateur
          </Button>
        </div>

        {/* Super Admin card */}
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="font-semibold">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <Badge className="bg-amber-500">Super Admin</Badge>
          </CardContent>
        </Card>

        {/* Admin list */}
        {admins.length === 0 && !loading ? (
          <Card className="bg-card/80"><CardContent className="p-8 text-center text-muted-foreground">Aucun administrateur créé</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {admins.map(admin => (
              <Card key={admin.id} className={`bg-card/80 ${admin.status !== 'active' ? 'opacity-50' : ''}`} data-testid={`admin-card-${admin.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold">
                        {(admin.first_name?.[0] || '')}{(admin.last_name?.[0] || '')}
                      </div>
                      <div>
                        <p className="font-semibold">{admin.first_name} {admin.last_name}</p>
                        <p className="text-xs text-muted-foreground">{admin.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={admin.status === 'active' ? 'default' : 'destructive'} className={admin.status === 'active' ? 'bg-green-500' : ''}>
                        {admin.status === 'active' ? 'Actif' : 'Désactivé'}
                      </Badge>
                      <Button variant="ghost" size="sm" title="Modifier le mot de passe"
                        onClick={() => setEditPassword({ id: admin.id, password: '' })} data-testid={`edit-pwd-${admin.id}`}>
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm"
                        className={admin.status === 'active' ? 'text-green-500 hover:text-red-500' : 'text-red-500 hover:text-green-500'}
                        onClick={() => handleToggleStatus(admin)} data-testid={`toggle-admin-${admin.id}`}>
                        <Power className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-400"
                        onClick={() => handleDeleteAdmin(admin)} data-testid={`delete-admin-${admin.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Password edit inline */}
                  {editPassword.id === admin.id && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2">
                      <Input type="text" value={editPassword.password} placeholder="Nouveau mot de passe"
                        onChange={(e) => setEditPassword(p => ({...p, password: e.target.value}))}
                        className="font-mono text-sm h-8 flex-1" data-testid={`pwd-input-${admin.id}`} />
                      <Button size="sm" onClick={handleSetPassword} data-testid={`save-pwd-${admin.id}`}>Enregistrer</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditPassword({ id: null, password: '' })}>Annuler</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nouvel administrateur</DialogTitle>
              <DialogDescription>Cet admin pourra tout faire sauf créer d&apos;autres admins, gérer les templates et les sauvegardes.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Prénom</Label>
                  <Input value={newAdmin.first_name} onChange={(e) => setNewAdmin(p => ({...p, first_name: e.target.value}))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nom</Label>
                  <Input value={newAdmin.last_name} onChange={(e) => setNewAdmin(p => ({...p, last_name: e.target.value}))} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input type="email" value={newAdmin.email} onChange={(e) => setNewAdmin(p => ({...p, email: e.target.value}))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Mot de passe</Label>
                <Input type="text" value={newAdmin.password} onChange={(e) => setNewAdmin(p => ({...p, password: e.target.value}))} className="font-mono" />
              </div>
              <Button className="w-full" onClick={handleCreate} style={{ backgroundColor: primaryColor }}>
                Créer l&apos;administrateur
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
